import { useEffect, useState } from 'react';
import { X, Printer } from '../../lib/icons';
import { fetchReleveCuve } from '../../lib/dataProvider';
import { fmt } from '../shared/badges';

const th = 'px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500';

// Relevé de compte d'huile d'une cuve : mouvements chronologiques, solde et PMP recalculés.
export const ReleveCuve = ({ cuve, onClose }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchReleveCuve(cuve.id).then(setData);
  }, [cuve.id]);

  const lignes = data?.lignes ?? [];

  return (
    <div data-testid="releve-cuve-overlay" className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-slate-900/60 p-4 print:static print:bg-white print:p-0">
      <style>{'@media print{body *{visibility:hidden}[data-testid="releve-cuve-doc"],[data-testid="releve-cuve-doc"] *{visibility:visible}[data-testid="releve-cuve-doc"]{position:absolute;left:0;top:0;width:100%}@page{size:A4 landscape;margin:10mm}}'}</style>
      <section data-testid="releve-cuve-doc" className="w-full max-w-6xl border border-slate-200 border-t-4 border-t-slate-900 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="font-heading text-lg font-black tracking-tight text-slate-900">
              Relevé de compte d’huile — {cuve.nom}
            </h2>
            <p className="font-data text-xs text-slate-500">
              {cuve.qualite} · {(cuve.famille ?? 'noble') === 'lampante' ? 'LAMPANTE' : 'NOBLE'} · capacité {fmt(cuve.capacite)} {cuve.unite} ·
              solde actuel {fmt(cuve.volume)} {cuve.unite} · PMP réel {fmt(data?.cmpReel ?? cuve.cmp ?? 0, 3)} TND/kg
            </p>
          </div>
          <div className="flex gap-2 print:hidden">
            <button
              type="button"
              data-testid="releve-cuve-imprimer"
              onClick={() => window.print()}
              className="flex h-10 items-center gap-2 border border-slate-300 px-3 text-sm font-bold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              <Printer size={15} /> Imprimer
            </button>
            <button type="button" data-testid="releve-cuve-close" onClick={onClose} className="border border-slate-200 p-2 text-slate-500 transition-colors duration-150 hover:bg-slate-900 hover:text-white">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50">
              <tr>
                {['Date', 'Mouvement', 'Pièce', 'Tiers', 'Entrée (kg)', 'Sortie (kg)', 'Prix unitaire', 'Solde volume', 'PMP après', 'Valeur stock'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!data && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-sm text-slate-500">Chargement…</td></tr>
              )}
              {data && lignes.length === 0 && (
                <tr><td colSpan={10} data-testid="releve-cuve-vide" className="px-3 py-8 text-center text-sm text-slate-500">Aucun mouvement enregistré sur cette cuve.</td></tr>
              )}
              {lignes.map((l) => (
                <tr key={l.id} data-testid={`releve-ligne-${l.id}`} className={`text-sm ${l.ajustement ? 'bg-amber-50' : l.ouverture ? 'bg-slate-50 font-semibold' : ''}`}>
                  <td className="px-2 py-1.5 font-data text-xs">{l.date}</td>
                  <td className="px-2 py-1.5 text-xs">{l.type}</td>
                  <td className="px-2 py-1.5 font-data text-xs font-semibold">{l.piece}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-600">{l.tiers || '—'}</td>
                  <td className="tabular px-2 py-1.5 font-data text-xs text-emerald-800">{l.sens === 'entree' ? fmt(l.kg, 3) : '—'}</td>
                  <td className="tabular px-2 py-1.5 font-data text-xs text-orange-800">{l.sens === 'sortie' ? fmt(l.kg, 3) : '—'}</td>
                  <td className="tabular px-2 py-1.5 font-data text-xs">{l.pu ? `${fmt(l.pu, 3)} TND` : '—'}</td>
                  <td className="tabular px-2 py-1.5 font-data text-xs font-semibold">{fmt(l.solde, 3)}</td>
                  <td className="tabular px-2 py-1.5 font-data text-xs font-bold">{l.pmp ? `${fmt(l.pmp, 3)} TND` : '—'}</td>
                  <td className="tabular px-2 py-1.5 font-data text-xs">{fmt(l.valeur, 3)} TND</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border-t border-slate-200 px-5 py-3">
          <p data-testid="releve-cuve-total" className="font-data text-xs text-slate-700">
            Solde final reconstitué : <b>{fmt(data?.soldeFinal ?? 0, 3)} {cuve.unite}</b> · valeur <b>{fmt(data?.valeurFinale ?? 0, 3)} TND</b> ·
            PMP reconstitué {fmt(lignes.length ? lignes[lignes.length - 1].pmp : 0, 3)} TND/kg (PMP réel de la cuve {fmt(data?.cmpReel ?? 0, 3)} TND/kg)
          </p>
          {(data?.ouverture ?? 0) >= 0.001 && (
            <p data-testid="releve-cuve-ouverture" className="mt-1 font-data text-[11px] text-slate-600">
              Le relevé démarre sur un solde d’ouverture de {fmt(data.ouverture, 3)} {cuve.unite} valorisé au PMP de la cuve :
              c’est le stock déjà en place avant les mouvements détaillés ci-dessus (mise en service, inventaire, campagne antérieure).
            </p>
          )}
          {Math.abs(data?.ecart ?? 0) >= 0.001 && (
            <p data-testid="releve-cuve-ecart" className="mt-1 font-data text-[11px] text-amber-800">
              Une ligne d’ajustement de {fmt(Math.abs(data.ecart), 3)} {cuve.unite} aligne le relevé sur le volume physique réel :
              les sorties enregistrées dépassent les entrées tracées.
            </p>
          )}
          <p className="mt-1 font-data text-[10px] text-slate-500">
            PMP recalculé après chaque mouvement : une entrée est valorisée à son prix d’achat, une sortie au PMP du moment (PMP inchangé).
          </p>
        </div>
      </section>
    </div>
  );
};
